'use client';

/**
 * **What a figure's confirmation can be cited against, with one owner**
 * (VALE-1032).
 *
 * The matter's documents plus, listed first, the attachments on this item's
 * institution's own replies (LF-5393) — resolved into the
 * `valueWithConfirmation` editor's `documents` options, together with the
 * placeholder that tells the picker's three empty states apart (LF-6314).
 *
 * It was assembled inside {@link useInventoryBlockData}, which was right while
 * the drawer and the add dialog were the only surfaces that could confirm a
 * figure. VALE-1032 puts the **inventory list** on the same popover, and a list
 * row needs this and nothing else in that hook — no institution roster, no
 * contacts, no entitlement seed. Extracting it is the *bind to the source of
 * truth* rule (root `CLAUDE.md`) applied before the second copy exists: the
 * order the two kinds of source appear in, the way an institution's reply names
 * whose reply it is, and the three-way placeholder are decided here and read
 * everywhere.
 *
 * ## `enabled` is the list's whole reason for the parameter
 *
 * The drawer holds one open record, so it fetches unconditionally. The list
 * holds every item on the matter at once, and `useValueConfirmationSources` is
 * **per item** — mounting it eagerly on each row would fire one request per
 * asset on a tab that is usually just being read. So a row asks only once its
 * reader opens the popover, and the matter-wide document query is shared through
 * react-query with whichever surface warmed it first.
 */

import { useMemo } from 'react';
import { useMatterFiles } from '@/lib/services/files';
import { useValueConfirmationSources } from '@/lib/services/inventory-items';
import type { AttributeOption } from '@/components/attributes';

export interface ValueSourceDocuments {
  /** The `valueWithConfirmation` editor's `documents` options, in citation order. */
  valueSourceDocuments: AttributeOption[];
  /** What the picker says with no options — loading / failed / genuinely none. */
  valueSourceDocumentsPlaceholder: string;
}

export function useValueSourceDocuments({
  matterId,
  itemId,
  enabled = true
}: {
  matterId: number;
  /**
   * The record whose institution replies can be cited. **Absent while adding**:
   * an unsaved item has no institution to have replied, so the list collapses to
   * the matter's own documents.
   */
  itemId?: number;
  /** `false` holds both queries — see the hook's own doc on why the list needs it. */
  enabled?: boolean;
}): ValueSourceDocuments {
  /*
    **The matter's documents are fetched with or without a record** (VALE-355). The
    add dialog has no `itemId` and can still confirm a figure against a document
    already on the matter, so gating this on a persisted id would put an empty
    picker in front of the one surface that most needs it. One matter-scoped query,
    shared through react-query with the Documents tab that usually warmed it.

    **Institution responses do need the record**, because they are the attachments
    on *this item's* institution's replies (LF-5393) — there is no such thing
    before the item has an institution. Absent while adding, which is why the
    grouping below collapses to a flat list there.
  */
  const {
    data: filesData,
    isLoading: loadingFiles,
    isError: filesFailed
  } = useMatterFiles(matterId, undefined, { enabled: enabled && matterId > 0 });
  const { data: valueSourcesData } = useValueConfirmationSources(
    matterId,
    itemId ?? 0,
    // `useValueConfirmationSources` refuses a non-persisted item itself
    // (VALE-1523) — the `-1` add-mode sentinel included — so this gate carries
    // only the drawer's own condition.
    { enabled }
  );

  const valueSourceDocuments = useMemo<AttributeOption[]>(() => {
    const named = (file: {
      id: number;
      displayName?: string | null;
      originalFilename: string;
    }) => ({
      value: String(file.id),
      label: file.displayName ?? file.originalFilename
    });

    /*
      An institution's own reply is the stronger citation, so those come first —
      and they say whose reply they are, because a `select` has no group headings
      to carry that the way `value-source-select-content.tsx` does on the flag-off
      form (LF-5393). Naming the institution inline is what keeps a statement the
      bank sent distinguishable from a file somebody uploaded.
    */
    const responses = (valueSourcesData?.institutionResponses ?? []).flatMap(
      (group) =>
        group.files.map((file) => ({
          value: String(file.id),
          label: `${named(file).label} (from ${group.institutionName})`
        }))
    );
    const documents = (filesData?.files ?? []).map(named);
    return [...responses, ...documents];
  }, [valueSourcesData, filesData]);

  /*
    The picker's whole empty state, and it has to tell three situations apart:
    still loading, the fetch failed, and a matter that genuinely has no documents.
    Collapsing the middle into the last is the defect LF-6314 fixed on the flag-off
    picker ("Link a document (optional)" over an empty list reads as "this matter
    has none"), so it is spelled out here rather than guessed at by the editor.
  */
  const valueSourceDocumentsPlaceholder = loadingFiles
    ? 'Loading documents…'
    : filesFailed
      ? 'Couldn’t load documents. Add a note instead'
      : 'No documents on this matter yet. Add a note instead';

  return { valueSourceDocuments, valueSourceDocumentsPlaceholder };
}
